// @ts-ignore
import { Power2, TweenMax } from 'gsap';
import React from 'react';
import styles from '../ScreenSaver.module.less';
import getVideoSrc from '../getVideoSrc';
import { translate } from '../../../services/L10n';
import classNames from 'classnames';
import emitter from '../../../emitter';
import { SCREENSAVER_STARTED } from '../../../settings/events';
import closeComponents from '../closeComponents';
import VideoPlayer from '../../VideoPlayer';
import { Icon } from '../../Icon';
import { t } from '../../../translations';

type HtmlDivEl = HTMLDivElement | null;

const SlideShow = () => {
  const pointer = React.useRef<HtmlDivEl>(null);
  const click = React.useRef<HtmlDivEl>(null);
  const playerArea = React.useRef<HtmlDivEl>(null);
  const header = React.useRef<HtmlDivEl>(null);
  const wrapper = React.useRef<HtmlDivEl>(null);
  const stepIndex = React.useRef<number>(0);

  const [isReady, setIsReady] = React.useState(false);

  React.useEffect(() => {
    setIsReady(false);
    if (isReady) {
      runNextStep();
      if (stepIndex.current + 1 === steps.length) startSlideShow();
    }
  }, [isReady]);

  /**
   * Sets next index based on steps
   */
  const calculateNextIndex = () =>
    stepIndex.current + 1 === steps.length ? 0 : stepIndex.current + 1;

  /**
   * Sets next index and runt step animations
   */
  const runNextStep = () => {
    stepIndex.current = calculateNextIndex();
    steps[stepIndex.current].animate();
  };

  /**
   * Adds a delay for animation to create brief pause achieve full animation
   */
  const delayNextStep = (callback = () => {}) => {
    wrapper.current &&
      TweenMax.to(wrapper.current, 0, {
        delay: steps[stepIndex.current].duration,
        onComplete: () => {
          setIsReady(true);
        },
      });
  };

  const { Player, play, pause, isPlaying } = VideoPlayer({
    src: getVideoSrc(),
  });

  /**
   * Sets up and starts slideshow
   */
  const startSlideShow = () => {
    emitter.emit(SCREENSAVER_STARTED);
    closeComponents();
    delayNextStep();
    play();
  };

  const showSlides = () => {
    playerArea.current &&
      TweenMax.to(playerArea.current, 0.6, {
        delay: 0,
        top: 0,
        ease: Power2.easeInOut,
      });

    delayNextStep();
  };

  const showTextWithPlayer = () => {
    if (!header.current || !playerArea.current) return;

    TweenMax.to(header.current, 0.6, {
      delay: 0,
      top: 130,
      ease: Power2.easeInOut,
    });

    TweenMax.to(playerArea.current, 0.6, {
      delay: 0,
      scale: 0.6,
      top: 130,
      ease: Power2.easeInOut,
    });

    delayNextStep();
  };

  const showPointer = () => {
    if (
      !header.current ||
      !playerArea.current ||
      !pointer.current ||
      !click.current
    )
      return;

    TweenMax.to(playerArea.current, 0.5, {
      delay: 0,
      opacity: 0.2,
      top: 1200,
      ease: Power2.easeIn,
      onComplete: function () {
        TweenMax.to(playerArea.current, 0.3, {
          delay: 0,
          scale: 1,
          opacity: 1,
        });
      },
    });

    TweenMax.to(header.current, 0.5, {
      delay: 0,
      top: -250,
      ease: Power2.easeIn,
    });

    TweenMax.to(pointer.current, 0.5, {
      delay: 0.5,
      scale: 1,
      opacity: 1,
      ease: Power2.easeInOut,
    });

    TweenMax.to(click.current, 0.5, {
      delay: 1.5,
      opacity: 1,
      ease: Power2.easeInOut,
      onComplete: function () {
        TweenMax.to(click.current, 0.3, {
          delay: 0,
          opacity: 0,
          ease: Power2.easeInOut,
          onComplete: function () {
            TweenMax.to(click.current, 0.3, {
              delay: 0,
              opacity: 1,
              ease: Power2.easeInOut,
              onComplete: function () {
                TweenMax.to(click.current, 0.3, {
                  delay: 0,
                  opacity: 0,
                  ease: Power2.easeInOut,
                  onComplete: function () {
                    TweenMax.to(click.current, 0.3, {
                      delay: 0,
                      opacity: 1,
                      ease: Power2.easeInOut,
                      onComplete: function () {
                        TweenMax.to(click.current, 0.3, {
                          delay: 0,
                          opacity: 0,
                          ease: Power2.easeInOut,
                        });
                      },
                    });
                  },
                });
              },
            });
          },
        });
      },
    });

    TweenMax.to(pointer.current, 0.5, {
      delay: 4.0,
      scale: 0.1,
      opacity: 0,
      ease: Power2.easeIn,
    });

    delayNextStep();
  };

  const steps = [
    {
      class: 'showSlides',
      duration: 9.3,
      animate: showSlides,
    },
    {
      class: 'withText',
      duration: 3,
      animate: showTextWithPlayer,
    },
    {
      class: 'slidesExit',
      duration: 4,
      animate: showPointer,
    },
    {
      class: 'slidesExit',
      duration: 0.5,
      animate: () => {},
    },
  ];

  const endSlideShow = () => isPlaying && pause();

  const SlideShowRenderer = () => (
    <div
      ref={wrapper}
      className={classNames(
        styles.slideshow,
        styles[steps[stepIndex.current].class]
      )}
    >
      <div className={styles.header} ref={header}>
        {translate(t.SCREENSAVER_TEXT)}
      </div>
      <div className={styles.playerArea} ref={playerArea}>
        {Player}
      </div>
      <div className={styles.pointer} ref={pointer}>
        <div className={styles.arc} ref={click} />
        <Icon className={styles.handPoint} iconName="HandPointIcon" />
      </div>
    </div>
  );

  return {
    SlideShowRenderer,
    endSlideShow,
    startSlideShow,
  };
};

export default SlideShow;
